import React, { useState } from 'react';
import { formatEtat, getEtatColor } from '../utils/etatFormatter';
import ObjetPhotoThumb from './ObjetPhotoThumb';

interface ObjetListTableProps {
  objets: any[];
  selectedIds: string[];
  onToggleSelection: (id: string) => void;
  onEdit: (objet: any) => void;
  canEdit?: boolean;
}

function locationLabel(objet: any): string {
  if (objet.box?.nom) return objet.box.nom;
  if (objet.zone?.nom) return objet.zone.nom;
  if (objet.piece?.nom) return objet.piece.nom;
  return '—';
}

function TruncatedNameButton({ nom, onOpen }: { nom: string; onOpen: () => void }) {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);

  const showTip = (el: HTMLElement) => {
    if (el.scrollWidth <= el.clientWidth + 1) {
      setTip(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const maxLeft = Math.max(8, window.innerWidth - 320);
    setTip({ x: Math.min(rect.left, maxLeft), y: rect.bottom + 6 });
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onMouseEnter={(e) => showTip(e.currentTarget)}
        onMouseLeave={() => setTip(null)}
        onFocus={(e) => showTip(e.currentTarget)}
        onBlur={() => setTip(null)}
        className="block max-w-[220px] truncate text-left font-medium text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        aria-label={`Ouvrir la fiche de ${nom}`}
      >
        {nom}
      </button>
      {tip && (
        <div
          role="tooltip"
          className="fixed z-50 max-w-sm whitespace-normal rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal text-white shadow-lg pointer-events-none"
          style={{ left: tip.x, top: tip.y }}
        >
          {nom}
        </div>
      )}
    </>
  );
}

const ObjetListTable: React.FC<ObjetListTableProps> = ({
  objets,
  selectedIds,
  onToggleSelection,
  onEdit,
  canEdit = true,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-10 px-3 py-3" />
            <th scope="col" className="w-16 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Photo
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Nom
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
              Catégorie
            </th>
            <th scope="col" className="w-16 px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Qté
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
              État
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">
              Localisation
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">
              SKU
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {objets.map((objet) => (
            <tr
              key={objet.id}
              className={`${canEdit ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                objet.besoinAchat ? 'bg-orange-50/60' : ''
              }`}
              onClick={canEdit ? () => onEdit(objet) : undefined}
            >
              <td className="px-3 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(objet.id)}
                  onChange={() => onToggleSelection(objet.id)}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                  aria-label={`Sélectionner ${objet.nom}`}
                />
              </td>
              <td className="px-3 py-3 align-middle">
                <ObjetPhotoThumb objet={objet} className="w-12 h-12" placeholder="📦" />
              </td>
              <td className="px-3 py-3 align-middle">
                <TruncatedNameButton nom={objet.nom} onOpen={() => onEdit(objet)} />
                <div className="text-xs text-gray-500 truncate max-w-[220px] lg:hidden">
                  {objet.categorie}
                  {objet.sousCategorie ? ` · ${objet.sousCategorie}` : ''}
                </div>
              </td>
              <td className="px-3 py-3 align-middle hidden lg:table-cell">
                <span className="text-sm text-gray-600 truncate block max-w-[180px]">
                  {objet.categorie}
                  {objet.sousCategorie ? ` · ${objet.sousCategorie}` : ''}
                </span>
              </td>
              <td className="px-3 py-3 align-middle text-center text-sm text-gray-700">{objet.quantite}</td>
              <td className="px-3 py-3 align-middle">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEtatColor(objet.etat)}`}
                >
                  {formatEtat(objet.etat)}
                </span>
              </td>
              <td className="px-3 py-3 align-middle hidden md:table-cell">
                <span className="text-sm text-gray-600 truncate block max-w-[160px]">{locationLabel(objet)}</span>
              </td>
              <td className="px-3 py-3 align-middle hidden xl:table-cell">
                <div className="flex items-center gap-2">
                  {objet.besoinAchat && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">🛒</span>
                  )}
                  <span className="text-xs text-gray-400 font-mono truncate max-w-[120px]">{objet.sku || '—'}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ObjetListTable;
